import { usePrincipalKey } from "@auth/sessionStore";
import { Button } from "@scribenote/brian-design-system";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@utils/format-date";
import { useEffect, useRef, useState } from "react";

import { QuestionAnswer } from "./QuestionAnswer";
import {
  answerText,
  inboxItemContext,
  QuestionRequestError,
  respond,
  type DeliveryAction,
  type InboxItem,
  type StaffResponse,
} from "./questionsClient";
import { questionKeys, useQuestion } from "./questionsQueries";

const OUTCOMES: Record<string, string> = {
  established: "Answered",
  moot: "No longer needed",
  superseded: "Replaced by another question",
};
const DECISIONS: Record<string, string> = {
  administration: "Treatment decision",
  bill: "Billing decision",
  note: "Note decision",
  other: "Decision awaiting an answer",
};

export function QuestionPanel({
  item,
  visible,
  onBack,
}: {
  item: InboxItem;
  visible: boolean;
  onBack: () => void;
}) {
  const principal = usePrincipalKey();
  const cache = useQueryClient();
  const detail = useQuestion(item.delivery.question_uuid);
  const question = detail.data?.question;
  const delivery = detail.data?.delivery;
  const handoff = delivery?.handoff as
    | { ranked_candidate_ranks?: unknown; unresolved_reasons?: unknown }
    | undefined;
  const [notice, setNotice] = useState("");
  const [dismissConfirm, setDismissConfirm] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (visible && question && !focused.current) {
      heading.current?.focus();
      focused.current = true;
    }
  }, [visible, question]);
  const attempts = useRef(new Map<string, DeliveryAction>());
  const refresh = () =>
    cache.invalidateQueries({ queryKey: questionKeys.all(principal) });
  const mutation = useMutation({
    meta: { handled: true },
    mutationFn: (body: StaffResponse | DeliveryAction) =>
      respond(item.delivery.question_uuid, body),
    onSuccess: async (_, body) => {
      setNotice(
        body.action === "reject"
          ? "Candidates rejected. The question remains open."
          : body.action === "none"
            ? "Recorded: none of these."
            : body.action === "partial"
              ? "Partial answer saved. The question remains open."
              : body.action === "dismissed"
                ? "Dismissed and returned for fallback routing. The question remains unresolved."
                : "Answer saved.",
      );
      setDismissConfirm(false);
      await refresh();
    },
    onError: async (error) => {
      if (
        error instanceof QuestionRequestError &&
        [409, 404, 422].includes(error.status)
      )
        await refresh();
    },
  });
  const surface = useMutation({
    meta: { handled: true },
    mutationFn: (body: DeliveryAction) =>
      respond(item.delivery.question_uuid, body),
    onSuccess: refresh,
  });
  const surfaceSend = surface.mutate;
  useEffect(() => {
    if (
      !visible ||
      !question ||
      !delivery ||
      delivery.state === "surfaced" ||
      delivery.state === "resolved" ||
      (question.status === "resolved" && !delivery.reviewing_resolution)
    )
      return;
    const node = heading.current;
    if (!node) return;
    const key = `${delivery.handoff_fingerprint}:${delivery.destination}:${delivery.destination_uuid ?? ""}`;
    let intersecting = false;
    const acknowledge = () => {
      if (
        !intersecting ||
        document.visibilityState !== "visible" ||
        attempts.current.has(key)
      )
        return;
      if (
        delivery.destination !== "role" &&
        delivery.destination !== "leadership"
      )
        return;
      const body: DeliveryAction = {
        operation_id: crypto.randomUUID(),
        action: "surfaced",
        destination: delivery.destination,
        destination_uuid: delivery.destination_uuid,
        presentation_id: crypto.randomUUID(),
        surface: "brian_bubble",
      };
      attempts.current.set(key, body);
      surfaceSend(body);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        intersecting = entry.isIntersecting && entry.intersectionRatio >= 1;
        acknowledge();
      },
      { threshold: 1 },
    );
    observer.observe(node);
    document.addEventListener("visibilitychange", acknowledge);
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", acknowledge);
    };
  }, [visible, question, delivery, surfaceSend]);

  if (detail.isError)
    return (
      <div className="p-4">
        <p role="alert">
          This question could not be loaded. It may no longer be available to
          you.
        </p>
        <Button variant="ghost" size="sm" onClick={() => void detail.refetch()}>
          Retry
        </Button>
        <Button variant="ghost" size="sm" onClick={onBack}>
          Back to questions
        </Button>
      </div>
    );
  if (!question)
    return (
      <p className="p-4" role="status">
        Loading question…
      </p>
    );
  const resolved = question.status === "resolved";
  const failed = mutation.isError;
  const retryable =
    failed &&
    (!(mutation.error instanceof QuestionRequestError) ||
      mutation.error.status >= 500);
  const blocked = mutation.isPending || retryable;
  const responseHistory =
    detail.data?.history.filter((event) =>
      ["response_recorded", "resolved", "superseded"].includes(event.kind),
    ) ?? [];
  return (
    <div className="p-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        ← All questions
      </Button>
      <p className="mt-3 text-xs text-annotation">{inboxItemContext(item)}</p>
      <h3
        ref={heading}
        tabIndex={-1}
        className="mt-1 font-display text-xl text-ink"
      >
        {question.prompt}
      </h3>
      <p className="mt-2 text-sm text-annotation">
        {DECISIONS[question.blocked_decision] ?? question.blocked_decision}
      </p>
      {question.deadline_at && (
        <p className="text-sm text-annotation">
          Needed by{" "}
          <time dateTime={question.deadline_at}>
            {formatDateTime(question.deadline_at)}
          </time>
        </p>
      )}
      {!resolved && (
        <p className="mt-3 text-xs font-semibold text-annotation">
          Awaiting answer
        </p>
      )}
      {!resolved &&
        Array.isArray(handoff?.unresolved_reasons) &&
        handoff.unresolved_reasons.length > 0 && (
          <ul className="mt-2 list-disc pl-4 text-sm">
            {handoff.unresolved_reasons
              .filter(
                (reason: unknown): reason is string =>
                  typeof reason === "string",
              )
              .map((reason: string, index: number) => (
                <li key={index}>{reason}</li>
              ))}
          </ul>
        )}
      {delivery?.reviewing_resolution && (
        <p className="mt-3 text-sm">
          New evidence is under review. The previous answer is preserved below.
        </p>
      )}
      {resolved ? (
        <div
          className="mt-4 rounded border border-rule bg-wall p-3"
          role="status"
        >
          <p className="font-semibold">
            {OUTCOMES[question.outcome ?? ""] ?? "Resolved"}
          </p>
          {question.outcome === "established" && (
            <p className="mt-1 whitespace-pre-wrap">
              {answerText(question.answer)}
            </p>
          )}
        </div>
      ) : (
        <QuestionAnswer
          key={question.revision}
          question={question}
          rankedRanks={
            Array.isArray(handoff?.ranked_candidate_ranks)
              ? handoff.ranked_candidate_ranks.filter(
                  (rank: unknown): rank is number => typeof rank === "number",
                )
              : []
          }
          disabled={blocked}
          onSubmit={(body) => {
            setNotice("");
            mutation.mutate({
              rejected_candidate_ranks: [],
              unresolved_reasons: [],
              ...body,
              expected_revision: question.revision,
              operation_id: crypto.randomUUID(),
            });
          }}
        />
      )}
      {notice && (
        <p role="status" className="mt-3 text-sm">
          {notice}
        </p>
      )}
      {failed && (
        <div role="alert" className="mt-3 text-sm">
          <p>
            {mutation.error instanceof QuestionRequestError
              ? mutation.error.message
              : "Your response could not be confirmed. Retry the same response."}
          </p>
          {retryable && (
            <Button
              size="sm"
              onClick={() =>
                mutation.variables && mutation.mutate(mutation.variables)
              }
            >
              Retry response
            </Button>
          )}
        </div>
      )}
      {surface.isError && (
        <div className="mt-3 text-sm" role="status">
          <p>Delivery acknowledgment could not be saved.</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              surface.variables && surface.mutate(surface.variables)
            }
          >
            Retry acknowledgment
          </Button>
        </div>
      )}
      {!resolved && delivery && (
        <div className="mt-4 border-t border-rule pt-3">
          {dismissConfirm ? (
            <>
              <p className="text-sm">
                Return this question for fallback routing? It will remain
                unanswered and may return here.
              </p>
              <div className="mt-2 flex gap-2">
                <Button
                  size="sm"
                  disabled={blocked}
                  onClick={() => {
                    if (
                      delivery.destination !== "role" &&
                      delivery.destination !== "leadership"
                    )
                      return;
                    mutation.mutate({
                      operation_id: crypto.randomUUID(),
                      action: "dismissed",
                      destination: delivery.destination,
                      destination_uuid: delivery.destination_uuid,
                      presentation_id: crypto.randomUUID(),
                      surface: "brian_bubble",
                    });
                  }}
                >
                  Dismiss question
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDismissConfirm(false)}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={blocked}
              onClick={() => setDismissConfirm(true)}
            >
              I can’t answer this
            </Button>
          )}
        </div>
      )}
      {(detail.data?.attempts ?? [])
        .filter(
          (attempt) =>
            attempt.uuid !== question.uuid && attempt.status === "resolved",
        )
        .map((attempt) => (
          <details
            key={attempt.uuid}
            className="mt-4 border-t border-rule pt-3"
          >
            <summary className="cursor-pointer text-sm font-semibold">
              Previous review · {OUTCOMES[attempt.outcome ?? ""] ?? "Resolved"}
            </summary>
            <p className="mt-2 text-sm whitespace-pre-wrap">
              {attempt.outcome === "established"
                ? answerText(attempt.answer)
                : "This review did not establish an answer."}
            </p>
          </details>
        ))}
      {responseHistory.length > 0 && (
        <details className="mt-4 border-t border-rule pt-3">
          <summary className="cursor-pointer text-sm font-semibold">
            Previous responses ({responseHistory.length})
          </summary>
          <ul className="mt-2 space-y-3">
            {responseHistory.map((event) => {
              const envelope = event.data as {
                command?: { input?: Record<string, unknown> };
              };
              const data = envelope?.command?.input ?? {};
              return (
                <li
                  key={event.uuid}
                  className="border-l-2 border-rule pl-3 text-sm"
                >
                  <p className="text-xs text-annotation">
                    {event.kind === "response_recorded"
                      ? "Partial response"
                      : "Resolution"}{" "}
                    · {formatDateTime(event.created_at)}
                  </p>
                  <p className="whitespace-pre-wrap">
                    {answerText(
                      data.basis ?? data.staff_statement ?? data.answer,
                    )}
                  </p>
                  {Array.isArray(data.unresolved_reasons) && (
                    <p>
                      Still unresolved:{" "}
                      {data.unresolved_reasons.map(answerText).join("; ")}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}
    </div>
  );
}
